import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { AppVersionService } from './app-version.service';

@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private readonly pollIntervalMs = 5 * 60 * 1000;
  private readonly snoozeMs = 5 * 60 * 1000;
  private initialBuildTime: string | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private snoozeTimer: ReturnType<typeof setTimeout> | null = null;

  readonly countdownSeconds = 60;

  readonly updateAvailable$ = new BehaviorSubject<boolean>(false);
  readonly secondsUntilReload$ = new BehaviorSubject<number>(this.countdownSeconds);

  constructor(private appVersionService: AppVersionService) {}

  init(): void {
    if (this.initialBuildTime !== null) return;

    // Going through load() latches the running build, which is what the admin
    // tools report back — the polling below reads the deployed file instead.
    this.appVersionService.load().then(buildInfo => {
      this.initialBuildTime = buildInfo?.buildTime ?? null;
      this.startPolling();
    });
  }

  reload(): void {
    this.stopCountdown();
    this.stopSnooze();
    this.reloadPage();
  }

  /** Hides the banner and brings it back, with a fresh countdown, in five minutes. */
  snooze(): void {
    this.stopCountdown();
    this.stopSnooze();
    this.updateAvailable$.next(false);
    this.snoozeTimer = setTimeout(() => {
      this.snoozeTimer = null;
      this.showUpdate();
    }, this.snoozeMs);
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.stopCountdown();
    this.stopSnooze();
  }

  private startPolling(): void {
    this.pollTimer = setInterval(() => this.checkForUpdate(), this.pollIntervalMs);
  }

  private stopPolling(): void {
    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private async checkForUpdate(): Promise<void> {
    const buildTime = await this.fetchBuildTime();
    if (buildTime && this.initialBuildTime && buildTime !== this.initialBuildTime) {
      this.stopPolling();
      this.showUpdate();
    }
  }

  private showUpdate(): void {
    this.updateAvailable$.next(true);
    this.startCountdown();
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.secondsUntilReload$.next(this.countdownSeconds);
    this.countdownTimer = setInterval(() => {
      const remaining = this.secondsUntilReload$.value - 1;
      this.secondsUntilReload$.next(Math.max(remaining, 0));
      if (remaining <= 0) {
        this.reload();
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private stopSnooze(): void {
    if (this.snoozeTimer !== null) {
      clearTimeout(this.snoozeTimer);
      this.snoozeTimer = null;
    }
  }

  /** Seam so tests can exercise reload() without reloading the test page. */
  protected reloadPage(): void {
    window.location.reload();
  }

  private async fetchBuildTime(): Promise<string | null> {
    const buildInfo = await this.appVersionService.fetchBuildInfo();
    return buildInfo?.buildTime ?? null;
  }
}
